import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "@/lib/auth";

const terms = [
  {
    key: "service",
    label: "[필수] 강스터디 이용약관 동의",
    detail:
      "제1조 (목적)\n이 약관은 강스터디가 제공하는 교육 서비스 및 경매 데이터 서비스 이용과 관련하여 강스터디와 회원 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.\n\n제2조 (정의)\n'서비스'란 강스터디가 제공하는 경매 물건 정보, 교육과정, 동영상 강의, 리포트, 상담 서비스 등 일체의 서비스를 의미합니다.",
  },
  {
    key: "privacy",
    label: "[필수] 개인정보 수집 및 이용 동의",
    detail:
      "수집하는 개인정보 항목: 이름, 이메일, 연락처\n수집 목적: 회원 관리, 서비스 제공, 상담 및 문의 대응\n보유 기간: 회원 탈퇴 시까지\n\n개인정보는 상담 예약 및 서비스 안내 목적으로만 사용되며, 이용자의 동의 없이 제3자에게 제공되지 않습니다.",
  },
  {
    key: "marketing",
    label: "[선택] 마케팅 정보 수신 동의",
    detail:
      "강스터디의 신규 강의, 할인 이벤트, 투자 정보 등 유용한 정보를 이메일과 문자로 받아보실 수 있습니다.\n\n수신 동의는 선택 사항이며, 동의하지 않으셔도 서비스 이용이 가능합니다. 언제든지 마이페이지에서 수신을 철회할 수 있습니다.",
  },
];

const formContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const formItem = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut" as const },
  },
};

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [agreements, setAgreements] = useState({ service: false, privacy: false, marketing: false });
  const [allAgreed, setAllAgreed] = useState(false);
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const { signUp } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleAllAgreed = () => {
    const newVal = !allAgreed;
    setAllAgreed(newVal);
    setAgreements({ service: newVal, privacy: newVal, marketing: newVal });
  };

  const handleSingleAgreement = (key: string) => {
    setAgreements((prev) => {
      const next = { ...prev, [key]: !prev[key as keyof typeof prev] };
      setAllAgreed(next.service && next.privacy && next.marketing);
      return next;
    });
  };

  const canNextStep1 = agreements.service && agreements.privacy;

  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "이름을 입력해주세요.";
    if (!form.phone.trim()) errs.phone = "연락처를 입력해주세요.";
    if (!form.email.trim()) errs.email = "이메일을 입력해주세요.";
    if (!form.password) errs.password = "비밀번호를 입력해주세요.";
    else if (form.password.length < 8) errs.password = "비밀번호는 8자 이상이어야 합니다.";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "비밀번호가 일치하지 않습니다.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setStatus("loading");
    // 이름·연락처는 auth.users.raw_user_meta_data 에 담는다.
    // public.users 미러는 handle_new_user 트리거가 id·email 만 채운다.
    const { error, needsEmailConfirm } = await signUp(form.email.trim(), form.password, {
      name: form.name,
      phone: form.phone,
    });
    if (error) {
      setErrors((prev) => ({ ...prev, email: error }));
      setStatus("error");
      return;
    }
    setNeedsEmailConfirm(Boolean(needsEmailConfirm));
    setStatus("success");
  };

  // Success State
  if (status === "success") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-[440px] mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
              className="bg-white rounded-2xl border border-[#E8E5E0] p-10 md:p-14"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 12 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-50 flex items-center justify-center"
              >
                <i className="ri-check-line text-3xl text-emerald-500"></i>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <h2 className="text-xl font-bold text-[#1C1917] mb-3">회원가입 완료!</h2>
                <p className="text-sm text-[#8C8780] mb-2 leading-relaxed">
                  <strong className="text-[#1C1917]">{form.name}</strong>님, 강스터디 회원이 되신 것을 환영합니다!
                </p>
                <p className="text-xs text-[#B8B3AE] mb-8">
                  {needsEmailConfirm
                    ? "가입 확인 메일을 보냈어요. 메일의 링크를 눌러 인증을 마치면 로그인할 수 있습니다."
                    : "지금 바로 로그인하고 무료 강의부터 시작해보세요."}
                </p>
                <Link
                  to="/login"
                  className="inline-block px-10 py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap"
                >
                  로그인하러 가기
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[500px] mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="text-center mb-10"
          >
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-lg bg-[#C4956A] flex items-center justify-center">
                <i className="ri-auction-line text-lg text-white"></i>
              </div>
              <span className="text-xl font-bold text-[#1C1917]">강스터디</span>
            </Link>
            <h1 className="text-2xl font-bold text-[#1C1917] mb-2">회원가입</h1>
            <p className="text-sm text-[#8C8780]">
              강스터디와 함께 경매 투자의 첫걸음을 시작하세요
            </p>
          </motion.div>

          {/* Step Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="flex items-center gap-2">
              <span className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-medium ${step === 1 ? "bg-[#1C1917] text-white" : "bg-emerald-500 text-white"}`}>
                {step === 1 ? "1" : <i className="ri-check-line"></i>}
              </span>
              <span className={`text-xs font-medium ${step === 1 ? "text-[#1C1917]" : "text-emerald-600"}`}>약관동의</span>
            </div>
            <div className={`w-8 h-px ${step === 2 ? "bg-[#1C1917]" : "bg-[#E8E5E0]"}`}></div>
            <div className="flex items-center gap-2">
              <span className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-medium ${step === 2 ? "bg-[#1C1917] text-white" : "bg-[#E8E5E0] text-[#8C8780]"}`}>2</span>
              <span className={`text-xs font-medium ${step === 2 ? "text-[#1C1917]" : "text-[#8C8780]"}`}>정보입력</span>
            </div>
          </motion.div>

          {/* Step 1: Terms Agreement */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" as const }}
                className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
              >
                <h3 className="text-base font-bold text-[#1C1917] mb-5">약관 동의</h3>

                {/* All Agree */}
                <label className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={allAgreed}
                    onChange={handleAllAgreed}
                    className="w-4 h-4 rounded accent-[#C4956A] cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-[#1C1917]">전체 동의하기</span>
                </label>

                <div className="space-y-3">
                  {terms.map((term) => (
                    <div key={term.key} className="border border-[#E8E5E0] rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={agreements[term.key as keyof typeof agreements]}
                            onChange={() => handleSingleAgreement(term.key)}
                            className="w-4 h-4 rounded accent-[#C4956A] cursor-pointer"
                          />
                          <span className="text-sm text-[#5C5650]">{term.label}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setExpandedTerm(expandedTerm === term.key ? null : term.key)}
                          className="w-6 h-6 flex items-center justify-center text-[#B8B3AE] hover:text-[#5C5650] transition-colors"
                        >
                          <motion.i
                            animate={{ rotate: expandedTerm === term.key ? 180 : 0 }}
                            transition={{ duration: 0.25 }}
                            className="ri-arrow-down-s-line text-xs"
                          ></motion.i>
                        </button>
                      </div>
                      <AnimatePresence>
                        {expandedTerm === term.key && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" as const }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4">
                              <div className="bg-[#FAFAF8] rounded-lg p-3 text-xs text-[#8C8780] leading-relaxed max-h-[150px] overflow-y-auto whitespace-pre-line">
                                {term.detail}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(2)}
                  disabled={!canNextStep1}
                  className="w-full mt-6 py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음 단계
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 2: Input Information */}
          <AnimatePresence mode="wait">
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" as const }}
                className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
              >
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1.5 text-sm text-[#8C8780] hover:text-[#C4956A] transition-colors mb-5"
                >
                  <i className="ri-arrow-left-line"></i>
                  약관 동의로 돌아가기
                </button>

                <h3 className="text-base font-bold text-[#1C1917] mb-5">회원 정보 입력</h3>

                <form onSubmit={handleSubmit}>
                  <motion.div variants={formContainer} initial="hidden" animate="visible" className="space-y-4">
                    {/* Name */}
                    <motion.div variants={formItem}>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                        이름 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="이름을 입력해주세요"
                        className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${errors.name ? "border-red-300 focus:border-red-400" : "border-[#E8E5E0] focus:border-[#C4956A]"}`}
                      />
                      {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                    </motion.div>

                    {/* Phone */}
                    <motion.div variants={formItem}>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                        연락처 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="010-0000-0000"
                        className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${errors.phone ? "border-red-300 focus:border-red-400" : "border-[#E8E5E0] focus:border-[#C4956A]"}`}
                      />
                      {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                    </motion.div>

                    {/* Email */}
                    <motion.div variants={formItem}>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                        이메일 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                        className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${errors.email ? "border-red-300 focus:border-red-400" : "border-[#E8E5E0] focus:border-[#C4956A]"}`}
                      />
                      {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                    </motion.div>

                    {/* Password */}
                    <motion.div variants={formItem}>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                        비밀번호 <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={form.password}
                          onChange={handleChange}
                          placeholder="8자 이상 입력해주세요"
                          className={`w-full px-4 pr-12 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${errors.password ? "border-red-300 focus:border-red-400" : "border-[#E8E5E0] focus:border-[#C4956A]"}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8780] hover:text-[#5C5650] transition-colors"
                        >
                          <i className={`text-sm ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                        </button>
                      </div>
                      {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
                    </motion.div>

                    {/* Confirm Password */}
                    <motion.div variants={formItem}>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                        비밀번호 확인 <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirm ? "text" : "password"}
                          name="confirmPassword"
                          value={form.confirmPassword}
                          onChange={handleChange}
                          placeholder="비밀번호를 다시 입력해주세요"
                          className={`w-full px-4 pr-12 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${errors.confirmPassword ? "border-red-300 focus:border-red-400" : "border-[#E8E5E0] focus:border-[#C4956A]"}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8780] hover:text-[#5C5650] transition-colors"
                        >
                          <i className={`text-sm ${showConfirm ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="text-xs text-red-400 mt-1">{errors.confirmPassword}</p>}
                    </motion.div>

                    <AnimatePresence>
                      {status === "error" && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                        >
                          회원가입 중 오류가 발생했습니다. 다시 시도해주세요.
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div variants={formItem}>
                      <motion.button
                        type="submit"
                        disabled={status === "loading"}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {status === "loading" ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            가입 중...
                          </>
                        ) : (
                          "회원가입 완료"
                        )}
                      </motion.button>
                    </motion.div>
                  </motion.div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Link */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="text-center mt-6 text-sm text-[#8C8780]"
          >
            이미 회원이신가요?{" "}
            <Link to="/login" className="text-[#C4956A] font-medium hover:text-[#B08050] transition-colors">
              로그인
            </Link>
          </motion.p>
        </div>
      </main>
      <Footer />
    </div>
  );
}